import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface FeedAuthor {
  name: string;
  url?: string;
}

export interface FeedItemSEO {
  meta_description?: string;
  meta_keywords?: string[];
}

export interface FeedItem {
  id: string;
  title: string;
  summary?: string;
  content_html: string;
  image?: string;
  url: string;
  tags?: string[];
  date_published: string;
  date_modified?: string;
  authors?: FeedAuthor[];
  language?: string;
  _seo?: FeedItemSEO;
  // computed
  slug: string;
  readingTime: number;
  source: "feed" | "database";
}

interface FeedCache {
  items: FeedItem[];
  fetchedAt: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let globalCache: FeedCache | null = null;

function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/, '');
}

function extractSlug(url: string, id: string, title?: string): string {
  if (title) return slugifyTitle(title);
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length > 0) return segments[segments.length - 1];
  } catch {}
  return id;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").replace(/&[^;]+;/g, " ");
}

function calcReadingTime(html: string): number {
  const words = stripHtml(html).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

async function fetchDatabasePosts(): Promise<FeedItem[]> {
  const { data, error } = await supabase
    .from("blog_posts")
    .select("id, title, slug, excerpt, content, featured_image, published_at, created_at, category_id")
    .eq("published", true)
    .order("published_at", { ascending: false });

  if (error || !data) {
    console.warn("Failed to fetch database blog posts:", error);
    return [];
  }

  return data.map((post) => ({
    id: `db-${post.id}`,
    title: post.title,
    summary: post.excerpt || undefined,
    content_html: post.content,
    image: post.featured_image || undefined,
    url: `https://ommni.io/blog/${post.slug}`,
    tags: [],
    date_published: post.published_at || post.created_at,
    authors: [{ name: "Ommni Team" }],
    slug: post.slug,
    readingTime: calcReadingTime(post.content),
    source: "database" as const,
  }));
}

export function useBlogFeed() {
  const [items, setItems] = useState<FeedItem[]>(globalCache?.items || []);
  const [loading, setLoading] = useState(!globalCache);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const fetchFeed = useCallback(async (force = false) => {
    if (fetchingRef.current) return;
    
    // Use cache if valid
    if (!force && globalCache && Date.now() - globalCache.fetchedAt < CACHE_TTL) {
      setItems(globalCache.items);
      setLoading(false);
      setError(null);
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Fetch from both sources in parallel
      const [feedResult, dbItems] = await Promise.all([
        (async () => {
          const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
          const url = `https://${projectId}.supabase.co/functions/v1/blog-feed`;
          const res = await fetch(url, {
            headers: { 'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          return (data.items || []).map((item: any) => ({
            ...item,
            slug: extractSlug(item.url || "", item.id, item.title),
            readingTime: calcReadingTime(item.content_html || ""),
            source: "feed" as const,
          })) as FeedItem[];
        })(),
        fetchDatabasePosts(),
      ]);

      // Merge and deduplicate by slug (feed takes priority)
      const slugSet = new Set<string>();
      const merged: FeedItem[] = [];

      for (const item of feedResult) {
        if (!slugSet.has(item.slug)) {
          slugSet.add(item.slug);
          merged.push(item);
        }
      }
      for (const item of dbItems) {
        if (!slugSet.has(item.slug)) {
          slugSet.add(item.slug);
          merged.push(item);
        }
      }

      // Sort by date descending
      merged.sort((a, b) => 
        new Date(b.date_published).getTime() - new Date(a.date_published).getTime()
      );

      globalCache = { items: merged, fetchedAt: Date.now() };
      setItems(merged);
      setError(null);
    } catch (err: any) {
      console.error("Failed to fetch blog feed:", err);
      setError("Couldn't load articles. Please try again.");
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  const retry = useCallback(() => fetchFeed(true), [fetchFeed]);

  const getBySlug = useCallback(
    (slug: string) => items.find((item) => item.slug === slug) || null,
    [items]
  );

  return { items, loading, error, retry, getBySlug };
}
